import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { AuthModalProvider, useAuthModal } from '@/contexts/AuthModalContext';
import { AuthModal } from '@/components/auth/AuthModal';
import { PasswordRecoveryDialog } from '@/components/auth/PasswordRecoveryDialog';
import { usePasswordRecovery } from '@/hooks/usePasswordRecovery';
import { useNewsletterPopup } from '@/hooks/useNewsletterPopup';
import { NewsletterPopupModal } from '@/components/NewsletterPopupModal';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { trackAffiliateClick } from '@/lib/analytics';


// Pages that render their own chrome (no shared Header/Footer)
const NO_CHROME_PATHS = ['/onboarding'];

const AppLayoutInner = () => {
  const { showAuthModal, closeAuthModal, openAuthModal } = useAuthModal();
  const { isRecoveryMode, clearRecoveryMode } = usePasswordRecovery();
  const location = useLocation();
  const { showPopup, dismissPopup, subscribeSuccess } = useNewsletterPopup(showAuthModal, location.pathname);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const noChrome = NO_CHROME_PATHS.includes(location.pathname);

  // Affiliate and Rewardful outbound links. The helper ignores every other href.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const href = target.closest('a')?.getAttribute('href');
      if (href) trackAffiliateClick(href);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // Disable browser's automatic scroll restoration so it doesn't override us
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Track previous pathname to detect actual route changes
  const prevPathname = useRef(location.pathname);

  // Scroll to top on route change using multiple strategies to ensure it works
  useEffect(() => {
    if (location.pathname !== prevPathname.current) {
      prevPathname.current = location.pathname;

      if (location.pathname.startsWith('/planner')) return;

      // Immediate scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Also after next frame in case layout hasn't settled
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
  }, [location.pathname]);

  return (
    <>
      {!noChrome && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-6">
          <Header
            user={user}
            onSignOut={async () => { await signOut(); navigate('/'); }}
            onShowAuthModal={openAuthModal}
            isRecoveryMode={isRecoveryMode}
          />
        </div>
      )}
      <Outlet />
      {!noChrome && <Footer />}
      {!noChrome && <div className="h-16 md:hidden" />}
      <AuthModal isOpen={showAuthModal} onClose={closeAuthModal} />
      <PasswordRecoveryDialog isOpen={isRecoveryMode} onClose={clearRecoveryMode} />
      <NewsletterPopupModal open={showPopup} onClose={dismissPopup} onSuccess={subscribeSuccess} />
    </>
  );
};

const AppLayout = () => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            retry: 1,
          },
        },
      })
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <AuthModalProvider>
              <Analytics />
              <SpeedInsights />
              <Toaster />
              <Sonner />
              <AppLayoutInner />
            </AuthModalProvider>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default AppLayout;
